import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatAxiosError } from "@/lib/format";
import { Building2, ArrowRight, ShieldCheck, FileText, Receipt } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && typeof user === "object") return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Bem-vindo ao DigiFácil");
      nav("/");
    } catch (err) {
      // v9.78: usar `formatAxiosError` para incluir contexto quando não há
      // response.data.detail (CORS/proxy/network). Antes exibia só "Erro
      // desconhecido. Tente novamente." mesmo em falhas totalmente
      // diagnosticáveis (401, 500, CORS bloqueado pelo private-app token).
      console.error("[Login] falha", err);
      toast.error(formatAxiosError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-slate-50">
      <div className="lg:col-span-3 relative hidden lg:block bg-gradient-to-br from-blue-50 via-white to-sky-50 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-200/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-sky-200/30 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4"></div>

        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-white border border-blue-200 shadow-sm grid place-items-center">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
            <span className="font-heading font-bold text-xl text-slate-900">DigiFácil</span>
          </div>

          <div className="max-w-xl">
            <div className="inline-block px-3 py-1 text-xs font-mono-fin uppercase tracking-widest bg-blue-100 text-blue-700 border border-blue-200 rounded-full mb-6">
              NF-e • NFS-e • Inter • Asaas
            </div>
            <h1 className="font-heading font-extrabold text-4xl xl:text-5xl leading-[1.05] tracking-tight mb-6 text-slate-900">
              A gestão da sua empresa,
              <span className="text-blue-600"> mais fácil que nunca</span>.
            </h1>
            <p className="text-slate-600 text-lg leading-relaxed mb-8">
              DigiFácil descomplica notas fiscais, boletos e cobranças
              recorrentes. Emita NF-e, receba via Banco Inter, Asaas e PIX,
              e envie tudo por e-mail em um clique — do seu jeito, sem
              burocracia.
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-start gap-2">
                <FileText className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-slate-900">NF-e / NFS-e</div>
                  <div className="text-xs text-slate-500">Simulação ou real via Focus/PlugNotas</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Receipt className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-slate-900">Boletos + PIX</div>
                  <div className="text-xs text-slate-500">Com QR Code integrado</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-slate-900">Recorrência</div>
                  <div className="text-xs text-slate-500">Cobrança mensal automática</div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-xs text-slate-500 font-mono-fin">
            v1.1 • Sandbox por padrão
          </div>
        </div>
      </div>

      <div className="lg:col-span-2 flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6">
          <div>
            <div className="flex items-center gap-2 lg:hidden mb-6">
              <Building2 className="w-6 h-6 text-blue-600" />
              <span className="font-heading font-bold text-xl">DigiFácil</span>
            </div>
            <h2 className="font-heading font-bold text-3xl mb-1 text-slate-900">Entrar</h2>
            <p className="text-slate-500 text-sm">Acesse o painel do seu ERP.</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                autoComplete="email"
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                className="mt-1.5"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold h-11"
            data-testid="login-submit"
            disabled={loading}
          >
            {loading ? "Entrando..." : "Entrar"}
            {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
        </form>
      </div>
    </div>
  );
}
